/**
 * Què recorda el navegador de Miranda, i de qui.
 *
 * F1 va desar la sessió del xat perquè un enllaç a `/reserves/:ids` o una recàrrega no
 * comencessin conversa nova. La revisió M1 hi va posar la condició: **les claus porten el
 * `userId`**. Al PC del mostrador hi entren en Pere i la Carme, i una clau compartida faria
 * que qui entrés segon heretés la conversa del primer. El backend també ho comprova
 * (`ChatSessionService.findSessionById` respon 404 si la sessió no és teva); això d'aquí
 * evita que s'hi arribi.
 *
 * Aquí només hi van identificadors: els missatges els guarda el backend.
 */

import {
  SINGLE_MIRANDA_AGENT_ID,
  nextStoredAgentStep,
  type AgentList,
} from './miranda-agents';

const PREFIX = 'miranda.';

/**
 * L'agent per defecte del giny lateral (si l'usuari no n'ha triat cap abans: el triat es desa a
 * `mirandaAgentKey`) i del selector de `/chat`. Una sola constant per a tots dos: la Miranda única
 * (en Pere, 14-OCT-2026). Amb el fre posat (`miranda` fora de `GET /agent/agents`), el valor que
 * queda és `router`: ho decideix `lib/miranda-agents.ts`, no aquesta constant.
 */
export const DEFAULT_AGENT_ID = SINGLE_MIRANDA_AGENT_ID;

export function mirandaSessionKey(userId: string): string {
  return `${PREFIX}${userId}.sessionId`;
}

export function mirandaAgentKey(userId: string): string {
  return `${PREFIX}${userId}.agentId`;
}

/** La marca de la migració d'una sola vegada dels agents vells a `miranda`. */
export function mirandaAgentMigratedKey(userId: string): string {
  return `${PREFIX}${userId}.agentMigrated`;
}

export function readMirandaStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null; // Safari en privat, o emmagatzematge bloquejat
  }
}

export function writeMirandaStored(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Sense persistència es continua igual: només es perd la conversa en recarregar.
  }
}

/**
 * Aplica a l'emmagatzematge el pas de `nextStoredAgentStep` (migració d'una sola vegada, o el fre)
 * i torna l'agent que ha de quedar al giny, o `null` si no canvia. Escriu la marca i, si l'agent
 * canvia, l'agent desat; la conversa la buida qui la crida (el context, amb `clearSession`).
 * Sense emmagatzematge (Safari en privat), la marca no queda i la migració es repeteix a cada
 * càrrega, que dona el mateix resultat.
 */
export function reconcileStoredAgent(
  userId: string,
  state: { agentId: string; hasSession: boolean },
  agents: AgentList,
): string | null {
  const migratedKey = mirandaAgentMigratedKey(userId);
  const step = nextStoredAgentStep(
    { ...state, migrated: readMirandaStored(migratedKey) === '1' },
    agents,
  );
  if (!step) return null;

  writeMirandaStored(migratedKey, step.migrated ? '1' : null);
  if (step.agentId === state.agentId) return null;
  writeMirandaStored(mirandaAgentKey(userId), step.agentId);
  return step.agentId;
}

/**
 * Esborra tot el que Miranda ha desat, de qualsevol usuari. Es crida al `logout`: el següent
 * que entri en aquest ordinador comença de zero, encara que sigui la mateixa persona.
 */
export function clearMirandaStorage(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Res a fer: si no es pot llegir l'emmagatzematge, tampoc s'hi ha desat res.
  }
}
